import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/lib/api';
import router from '@/router';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'SUPERVISOR' | 'ASSISTANT' | 'FAMILY';
  cpf?: string;
  active: boolean;
}

export interface LoginOptions {
  remember: boolean;
}

type AuthStorageKind = 'local' | 'session';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

function storageFor(kind: AuthStorageKind): Storage {
  return kind === 'local' ? localStorage : sessionStorage;
}

function detectInitialStorage(): AuthStorageKind | null {
  if (localStorage.getItem(ACCESS_TOKEN_KEY)) return 'local';
  if (sessionStorage.getItem(ACCESS_TOKEN_KEY)) return 'session';
  return null;
}

function clearStoredTokens() {
  for (const storage of [localStorage, sessionStorage]) {
    storage.removeItem(ACCESS_TOKEN_KEY);
    storage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export const useAuthStore = defineStore('auth', () => {
  const initialStorage = detectInitialStorage();
  const activeStorage = ref<AuthStorageKind | null>(initialStorage);
  const user = ref<User | null>(null);
  const accessToken = ref<string | null>(
    initialStorage ? storageFor(initialStorage).getItem(ACCESS_TOKEN_KEY) : null,
  );
  const refreshToken = ref<string | null>(
    initialStorage ? storageFor(initialStorage).getItem(REFRESH_TOKEN_KEY) : null,
  );
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => !!accessToken.value);
  const userRole = computed(() => user.value?.role);
  const isAdmin = computed(() => userRole.value === 'ADMIN');
  const isSupervisor = computed(() => userRole.value === 'SUPERVISOR');
  const isAssistant = computed(() => userRole.value === 'ASSISTANT');
  const isFamily = computed(() => userRole.value === 'FAMILY');

  function persistTokens(access: string, refresh: string, kind: AuthStorageKind) {
    clearStoredTokens();
    const storage = storageFor(kind);
    storage.setItem(ACCESS_TOKEN_KEY, access);
    storage.setItem(REFRESH_TOKEN_KEY, refresh);
    activeStorage.value = kind;
  }

  async function login(email: string, password: string, options: LoginOptions) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post('/auth/login', { email, password });
      const data = response.data;

      user.value = data.user;
      accessToken.value = data.accessToken;
      refreshToken.value = data.refreshToken;
      persistTokens(data.accessToken, data.refreshToken, options.remember ? 'local' : 'session');

      api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;

      // Don't push to router here, let the component handle it
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Login failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    // A local logout before a successful login has no token to invalidate.
    // Avoid a needless protected request that would return 401.
    if (accessToken.value) {
      try {
        await api.post('/auth/logout');
      } catch {
        // Continue with local logout even if the server session is unavailable.
      }
    }

    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    activeStorage.value = null;
    clearStoredTokens();

    delete api.defaults.headers.common['Authorization'];

    await router.push('/login');
  }

  async function fetchCurrentUser() {
    if (!accessToken.value) return;

    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken.value}`;
      const response = await api.get('/auth/me');
      user.value = response.data;
    } catch (err) {
      console.error('Failed to fetch current user:', err);
      await logout();
    }
  }

  async function refreshAccessToken() {
    if (!refreshToken.value) {
      await logout();
      return;
    }

    try {
      const response = await api.post('/auth/refresh', {
        refreshToken: refreshToken.value,
      });

      accessToken.value = response.data.accessToken;
      refreshToken.value = response.data.refreshToken;
      persistTokens(
        response.data.accessToken,
        response.data.refreshToken,
        activeStorage.value || 'session',
      );

      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
    } catch {
      await logout();
    }
  }

  function updateUser(updatedUser: User) {
    user.value = updatedUser;
  }

  return {
    user,
    accessToken,
    loading,
    error,
    isAuthenticated,
    userRole,
    isAdmin,
    isSupervisor,
    isAssistant,
    isFamily,
    login,
    logout,
    fetchCurrentUser,
    refreshAccessToken,
    updateUser,
  };
});
